/**
 * Checklist Review Component
 *
 * Second checker view for reviewing and approving/rejecting a completed run.
 */

import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router";
import type { AxiosError } from "axios";
import { useWindowId } from "@/components/ui/os-layout/OsWindow";
import { useOsActionsOptional } from "@/components/ui/os-layout/OsContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleNotch,
  faCheck,
  faTimes,
  faExclamationTriangle,
  faUser,
  faClock,
  faRobot,
} from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import { Badge } from "@/components/ui/badge/Badge";
import { useNotification } from "@/context/NotificationContext";
import {
  getRun,
  getChecklist,
  reviewRun,
  addStepComment,
  getStepStatusBgClass,
  getStepStatusColor,
  type ChecklistRun,
  type Checklist,
  type StepResponse,
  OPS_TEAM_MEMBERS,
} from "@/services/api/opsChecklists";
import { AuthenticatedImage } from "./AuthenticatedImage";
import { ChecklistBulletPoints } from "./ChecklistBulletPoints";
import { ChecklistInstructionsMarkdown } from "./ChecklistInstructionsMarkdown";
import { OpsSubpageHeadingClassic } from "@/components/ops/OpsSubpageHeadingClassic";

type OpsChecklistErrorPayload = {
  detail?: unknown;
  error?: {
    message?: string;
    details?: { validation_errors?: Array<{ msg?: string }> };
  };
};

/** Unwraps ani_api `create_error_response` / validation_error_handler payloads. */
function formatOpsChecklistClientError(error: unknown): string | null {
  const ax = error as AxiosError<OpsChecklistErrorPayload>;
  const data = ax.response?.data;
  if (!data || typeof data !== "object") return null;

  const validationErrors = data.error?.details?.validation_errors;
  if (Array.isArray(validationErrors) && validationErrors.length > 0) {
    return validationErrors
      .map((row) => (typeof row.msg === "string" ? row.msg : JSON.stringify(row)))
      .join("; ");
  }

  const d = data.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) {
    return d
      .map((item) =>
        typeof item === "object" && item !== null && "msg" in item
          ? String((item as { msg?: string }).msg)
          : JSON.stringify(item),
      )
      .join("; ");
  }
  if (typeof data.error?.message === "string") return data.error.message;
  return null;
}

interface StepReviewCardProps {
  step: Checklist["steps"][0];
  response: StepResponse;
  runId: string;
  reviewer: string;
  onRunUpdated: (updatedRun: ChecklistRun) => void;
}

function StepReviewCard({
  step,
  response,
  runId,
  reviewer,
  onRunUpdated,
}: StepReviewCardProps) {
  const { addNotification } = useNotification();
  const [isAddingComment, setIsAddingComment] = useState(false);
  const [comment, setComment] = useState(response.reviewer_comment || "");
  const [isSaving, setIsSaving] = useState(false);
  const [decisionSavingKind, setDecisionSavingKind] = useState<"accept" | "reject" | null>(
    null,
  );
  const [showLightbox, setShowLightbox] = useState(false);
  const statusColor = getStepStatusColor(response.status);
  const statusBg = getStepStatusBgClass(response.status);

  const handleSaveComment = async () => {
    if (!comment.trim()) return;
    
    setIsSaving(true);
    try {
      const updatedRun = await addStepComment(runId, step.step_id, { comment: comment.trim() });
      onRunUpdated(updatedRun);
      setIsAddingComment(false);
    } catch (error) {
      console.error("Failed to save comment:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleStepDecision = async (accepted: boolean) => {
    if (!reviewer.trim()) return;
    setDecisionSavingKind(accepted ? "accept" : "reject");
    try {
      // Always send `comment` in the JSON body: strict validators or some deployed stacks
      // may require the key (422 "Field required"). Empty string is ignored by the API when
      // persisting if a step decision is present.
      const note =
        comment.trim() ||
        (typeof response.reviewer_comment === "string"
          ? response.reviewer_comment.trim()
          : "");
      const updatedRun = await addStepComment(runId, step.step_id, {
        reviewer_step_accepted: accepted,
        comment: note || "",
      });
      onRunUpdated(updatedRun);
    } catch (error) {
      console.error("Failed to save step review decision:", error);
      const ax = error as AxiosError<OpsChecklistErrorPayload>;
      const status = ax.response?.status;
      const formatted = formatOpsChecklistClientError(error);
      const raw =
        typeof ax.response?.data === "object" && ax.response.data !== null
          ? JSON.stringify(ax.response.data)
          : null;
      addNotification({
        type: "error",
        title: "Step review",
        message:
          formatted ||
          (status === 422 && raw ? raw : null) ||
          (status ? `Request failed (HTTP ${status})` : null) ||
          "Failed to save step accept/reject",
      });
    } finally {
      setDecisionSavingKind(null);
    }
  };

  const stepDecision = response.reviewer_step_accepted;

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-3 p-4 border-b border-slate-200 dark:border-slate-700">
        <div className={`w-3 h-3 rounded-full flex-shrink-0 ${statusBg}`} />
        <div className="flex-1 min-w-0">
          <h3 className="font-medium text-slate-900 dark:text-white">
            {step.order}. {step.title}
          </h3>
        </div>
        {/* Only show Timmy warning if user ignored it */}
        {response.ai_review_ignored && response.ai_review_rating !== "green" && (
          <span 
            className={response.ai_review_rating === "red" 
              ? "text-red-500 dark:text-red-400" 
              : "text-amber-500 dark:text-amber-400"
            }
            title={`Timmy's note: ${response.ai_review_feedback}`}
          >
            <FontAwesomeIcon icon={faRobot} className="text-sm" />
          </span>
        )}
        <Badge color={statusColor}>
          {response.status.charAt(0).toUpperCase() + response.status.slice(1)}
        </Badge>
      </div>

      {/* Instructions (collapsible) */}
      <details className="group">
        <summary className="px-4 py-2 text-sm text-slate-500 dark:text-slate-400 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/50">
          View instructions
        </summary>
        <div className="px-4 pb-3 text-sm">
          <ChecklistInstructionsMarkdown
            content={step.instructions}
            className="!text-slate-600 dark:!text-slate-300"
          />
        </div>
      </details>

      {/* Response */}
      <div className="p-4 bg-slate-50 dark:bg-slate-800/50">
        <div className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
          Evidence / Notes
        </div>
        <div className="text-sm text-slate-600 dark:text-slate-400 whitespace-pre-wrap">
          {response.notes || <em className="text-slate-400">No notes provided</em>}
        </div>

        {/* Screenshot evidence */}
        {response.screenshot_url && (
          <div className="mt-3">
            <div className="text-xs text-slate-500 dark:text-slate-400 mb-1">Screenshot (click to expand):</div>
            <button onClick={() => setShowLightbox(true)} className="block">
              <AuthenticatedImage 
                src={response.screenshot_url}
                alt="Evidence screenshot" 
                className="max-h-40 rounded-lg border border-slate-200 dark:border-slate-700 cursor-pointer hover:opacity-90 transition-opacity"
              />
            </button>
          </div>
        )}

        {/* Screenshot Lightbox */}
        {showLightbox && response.screenshot_url && (
          <div 
            className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
            onClick={() => setShowLightbox(false)}
          >
            <div className="relative max-w-5xl max-h-[90vh]">
              <AuthenticatedImage 
                src={response.screenshot_url}
                alt="Evidence screenshot" 
                className="max-w-full max-h-[90vh] rounded-lg object-contain"
              />
              <button
                onClick={() => setShowLightbox(false)}
                className="absolute top-2 right-2 w-8 h-8 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center transition-colors"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
          </div>
        )}

        {/* AMBER/RED-specific info */}
        {(response.status === "red" || response.status === "amber") && response.assigned_to && (
          <div className={`mt-3 p-3 rounded-lg ${
            response.status === "red" 
              ? "bg-red-50 dark:bg-red-900/20" 
              : "bg-amber-50 dark:bg-amber-900/20"
          }`}>
            <div className={`flex items-center gap-2 text-sm ${
              response.status === "red" 
                ? "text-red-700 dark:text-red-400" 
                : "text-amber-700 dark:text-amber-400"
            }`}>
              <FontAwesomeIcon icon={faExclamationTriangle} />
              <span>
                Assigned to <strong>{response.assigned_to}</strong> for
                remediation
              </span>
            </div>
            {response.flagged_confirmed && (
              <div className={`text-xs mt-1 ${
                response.status === "red" 
                  ? "text-red-600 dark:text-red-500" 
                  : "text-amber-600 dark:text-amber-500"
              }`}>
                <FontAwesomeIcon icon={faCheck} className="mr-1" />
                Confirmed flagged to assignee
              </div>
            )}
          </div>
        )}

        {/* Timestamp */}
        <div className="mt-2 text-xs text-slate-400 flex items-center gap-1">
          <FontAwesomeIcon icon={faClock} />
          {new Date(response.completed_at).toLocaleString()}
        </div>
      </div>

      {/* Timmy's AI review feedback - only show if user ignored the warning */}
      {response.ai_review_ignored && response.ai_review_rating !== "green" && response.ai_review_feedback && (
        <div className="p-3 border-t border-slate-200 dark:border-slate-700">
          <div className="flex items-start gap-2 text-sm text-slate-600 dark:text-slate-300">
            <Badge color={response.ai_review_rating === "red" ? "red" : "amber"} size="sm">
              <FontAwesomeIcon icon={faRobot} className="mr-1" />
              Timmy
            </Badge>
            <span className="flex-1">{response.ai_review_feedback}</span>
          </div>
        </div>
      )}

      {/* Per-step second-checker accept / reject */}
      <div className="p-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50/90 dark:bg-slate-900/25">
        <div className="text-sm font-medium text-slate-800 dark:text-slate-200 mb-2">
          Second reviewer — this step
        </div>
        {!reviewer.trim() ? (
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Select your name below to accept or reject this step.
          </p>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2 mb-3">
              {stepDecision === true && (
                <Badge color="green" size="sm">
                  Accepted
                </Badge>
              )}
              {stepDecision === false && (
                <Badge color="red" size="sm">
                  Rejected
                </Badge>
              )}
              {(stepDecision === undefined || stepDecision === null) && (
                <Badge color="zinc" size="sm">
                  Not reviewed
                </Badge>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => handleStepDecision(true)}
                disabled={decisionSavingKind !== null || stepDecision === true}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-green-600 text-green-700 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-950/40 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {decisionSavingKind === "accept" ? (
                  <FontAwesomeIcon icon={faCircleNotch} className="animate-spin" />
                ) : (
                  <FontAwesomeIcon icon={faCheck} />
                )}
                Accept step
              </button>
              <button
                type="button"
                onClick={() => handleStepDecision(false)}
                disabled={decisionSavingKind !== null || stepDecision === false}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-red-500 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {decisionSavingKind === "reject" ? (
                  <FontAwesomeIcon icon={faCircleNotch} className="animate-spin" />
                ) : (
                  <FontAwesomeIcon icon={faTimes} />
                )}
                Reject step
              </button>
            </div>
          </>
        )}
      </div>

      {/* Reviewer comment section */}
      <div className="p-4 border-t border-slate-200 dark:border-slate-700">
        {response.reviewer_comment && !isAddingComment ? (
          <div>
            <div className="text-sm font-medium text-indigo-800 dark:text-indigo-300 mb-1">
              Reviewer Comment
            </div>
            <div className="text-sm text-slate-600 dark:text-slate-400 whitespace-pre-wrap mb-2">
              {response.reviewer_comment}
            </div>
            <button
              onClick={() => setIsAddingComment(true)}
              className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline"
            >
              Edit comment
            </button>
          </div>
        ) : isAddingComment ? (
          <div>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Add a comment on this step..."
              className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
              rows={2}
              autoFocus
            />
            <div className="flex gap-2 mt-2">
              <button
                onClick={handleSaveComment}
                disabled={!comment.trim() || isSaving}
                className="px-3 py-1 text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors disabled:opacity-50"
              >
                {isSaving ? "Saving..." : "Save"}
              </button>
              <button
                onClick={() => {
                  setIsAddingComment(false);
                  setComment(response.reviewer_comment || "");
                }}
                className="px-3 py-1 text-xs font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setIsAddingComment(true)}
            className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline"
          >
            + Add comment
          </button>
        )}
      </div>
    </div>
  );
}

interface ReviewDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onReview: (approved: boolean, notes: string) => void;
  isLoading: boolean;
  action: "approve" | "reject";
}

function ReviewDialog({
  isOpen,
  onClose,
  onReview,
  isLoading,
  action,
}: ReviewDialogProps) {
  const [notes, setNotes] = useState("");

  if (!isOpen) return null;

  const isApprove = action === "approve";

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-slate-800 rounded-xl p-6 max-w-md w-full mx-4 shadow-xl">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
          {isApprove ? "Approve checklist" : "Reject checklist"}
        </h3>

        <div className="mb-6">
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            {isApprove ? "Notes (optional)" : "Reason for rejection *"}
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={
              isApprove
                ? "Any comments for the record..."
                : "Describe what needs to be fixed..."
            }
            className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
            rows={4}
          />
        </div>

        <div className="flex gap-3 justify-end">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => onReview(isApprove, notes)}
            disabled={(!isApprove && !notes.trim()) || isLoading}
            className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 ${
              isApprove
                ? "bg-green-600 hover:bg-green-700"
                : "bg-red-600 hover:bg-red-700"
            }`}
          >
            {isLoading && (
              <FontAwesomeIcon icon={faCircleNotch} className="animate-spin" />
            )}
            <FontAwesomeIcon icon={isApprove ? faCheck : faTimes} />
            {isApprove ? "Approve" : "Reject"}
          </button>
        </div>
      </div>
    </div>
  );
}

export interface ChecklistReviewProps {
  /**
   * When the review UI is shown inside an aniOS window, the run id is passed here.
   */
  windowRunId?: string;
}

export function ChecklistReview(props: ChecklistReviewProps = {}) {
  const { windowRunId } = props;
  const { runId: runIdParam } = useParams<{ runId: string }>();
  const runId = windowRunId ?? runIdParam;
  const navigate = useNavigate();
  const windowId = useWindowId();
  const osActions = useOsActionsOptional();
  const { addNotification } = useNotification();

  const goToChecklistDashboard = useCallback(() => {
    if (windowId && osActions) {
      osActions.updateWindow(windowId, {
        params: {},
        title: "Ops Checklists",
      });
      return;
    }
    if (windowId && import.meta.env.DEV) {
      console.warn(
        "[ChecklistReview] windowId is set but OsActions context is missing; falling back to navigate (aniOS window may not update).",
      );
    }
    navigate("/ops/checklists");
  }, [windowId, osActions, navigate]);

  const [run, setRun] = useState<ChecklistRun | null>(null);
  const [checklist, setChecklist] = useState<Checklist | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Review dialog
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [reviewAction, setReviewAction] = useState<"approve" | "reject">("approve");
  const [isReviewing, setIsReviewing] = useState(false);

  // Reviewer selection
  const [reviewer, setReviewer] = useState<string>("");
  const lastSyncedRunIdRef = useRef<string | undefined>(undefined);

  const loadData = useCallback(async () => {
    if (!runId) return;

    try {
      const runData = await getRun(runId);
      setRun(runData);

      const checklistData = await getChecklist(runData.checklist_id);
      setChecklist(checklistData);
    } catch (error) {
      console.error("Failed to load run:", error);
      addNotification({
        type: "error",
        title: "Checklist",
        message: "Failed to load checklist run",
      });
    } finally {
      setIsLoading(false);
    }
  }, [runId, addNotification]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!run) return;
    if (lastSyncedRunIdRef.current !== run.run_id) {
      lastSyncedRunIdRef.current = run.run_id;
      setReviewer(run.second_checker ?? "");
    }
  }, [run]);

  const handleReview = async (approved: boolean, notes: string) => {
    if (!run || !reviewer) return;

    setIsReviewing(true);
    try {
      await reviewRun(run.run_id, reviewer, {
        approved,
        notes: notes || undefined,
      });

      addNotification({
        type: "success",
        title: "Checklist",
        message: approved ? "Checklist approved" : "Checklist rejected",
      });
      goToChecklistDashboard();
    } catch (error) {
      console.error("Failed to review:", error);
      addNotification({
        type: "error",
        title: "Checklist",
        message: "Failed to submit review",
      });
    } finally {
      setIsReviewing(false);
    }
  };

  if (isLoading || !run || !checklist) {
    return (
      <div className="flex items-center justify-center h-64">
        <FontAwesomeIcon
          icon={faCircleNotch}
          className="text-2xl text-indigo-500 animate-spin"
        />
      </div>
    );
  }

  // Count by status
  const statusCounts = {
    green: run.steps.filter((s) => s.status === "green").length,
    amber: run.steps.filter((s) => s.status === "amber").length,
    red: run.steps.filter((s) => s.status === "red").length,
  };

  const stepsAcceptedCount = checklist.steps.filter((step) => {
    const r = run.steps.find((s) => s.step_id === step.step_id);
    return r?.reviewer_step_accepted === true;
  }).length;
  const allStepsAccepted = stepsAcceptedCount === checklist.steps.length;

  return (
    <div className="flex flex-col h-full min-h-0 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-shrink-0 gap-4">
        <OpsSubpageHeadingClassic
          title={`Review: ${run.checklist_name}`}
          titleClassName="text-lg font-bold text-slate-900 dark:text-white"
          subtitle={
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {run.date} • Completed by {run.primary_user}
            </p>
          }
          onBackClick={goToChecklistDashboard}
          backButtonAriaLabel="Back to checklists"
          backLinkDataTestId="checklist-review-back"
          linkClassName="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
        />
        <Badge color="amber" className="flex-shrink-0">
          Awaiting Review
        </Badge>
      </div>

      <ChecklistBulletPoints
        items={checklist.bullet_points}
        className="mb-6 flex-shrink-0"
      />

      {/* Summary stats */}
      <div className="grid grid-cols-3 gap-4 mb-6 flex-shrink-0">
        <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-green-600 dark:text-green-400">
            {statusCounts.green}
          </div>
          <div className="text-xs text-green-700 dark:text-green-500">Green</div>
        </div>
        <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-amber-600 dark:text-amber-400">
            {statusCounts.amber}
          </div>
          <div className="text-xs text-amber-700 dark:text-amber-500">Amber</div>
        </div>
        <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-red-600 dark:text-red-400">
            {statusCounts.red}
          </div>
          <div className="text-xs text-red-700 dark:text-red-500">Red</div>
        </div>
      </div>

      {/* Previous rejections */}
      {run.rejection_count > 0 && (
        <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4 mb-6 flex-shrink-0">
          <div className="text-sm font-medium text-red-700 dark:text-red-400 mb-2">
            <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
            Previously Rejected ({run.rejection_count} time
            {run.rejection_count > 1 ? "s" : ""})
          </div>
          {run.rejection_notes.map((note, i) => (
            <div
              key={i}
              className="text-sm text-red-600 dark:text-red-500 mt-1 pl-4 border-l-2 border-red-300 dark:border-red-700"
            >
              {note}
            </div>
          ))}
        </div>
      )}

      {/* Step reviews */}
      <div className="flex-1 min-h-0 overflow-auto space-y-4">
        {checklist.steps.map((step) => {
          const response = run.steps.find((r) => r.step_id === step.step_id);
          if (!response) return null;

          return (
            <StepReviewCard
              key={step.step_id}
              step={step}
              response={response}
              runId={run.run_id}
              reviewer={reviewer}
              onRunUpdated={setRun}
            />
          );
        })}
      </div>

      {/* Footer actions */}
      <div className="mt-4 pt-4 border-t-2 border-slate-200 dark:border-slate-600 flex-shrink-0 bg-slate-50 dark:bg-slate-800/50 -mx-6 px-6 pb-2 rounded-b-xl">
        {/* Reviewer selection */}
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <label className="text-sm font-medium text-slate-700 dark:text-slate-300 flex items-center gap-2">
              <FontAwesomeIcon icon={faUser} />
              Reviewer:
            </label>
            <select
              value={reviewer}
              onChange={(e) => setReviewer(e.target.value)}
              className="px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent min-w-[160px]"
            >
              <option value="">Select your name...</option>
              {OPS_TEAM_MEMBERS.filter((m) => m !== run.primary_user).map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => {
                setReviewAction("reject");
                setShowReviewDialog(true);
              }}
              disabled={!reviewer}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-600 dark:text-red-400 border border-red-300 dark:border-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FontAwesomeIcon icon={faTimes} />
              Reject checklist
            </button>
            <button
              onClick={() => {
                setReviewAction("approve");
                setShowReviewDialog(true);
              }}
              disabled={!reviewer || !allStepsAccepted}
              title={
                !allStepsAccepted
                  ? "Accept every step individually before approving the checklist"
                  : undefined
              }
              className="flex items-center gap-2 px-5 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FontAwesomeIcon icon={faCheck} />
              Approve checklist
            </button>
          </div>
        </div>
        <p className="text-xs text-slate-500 dark:text-slate-400 mt-3">
          Final approval is enabled when every step is individually accepted (
          {stepsAcceptedCount}/{checklist.steps.length}).
        </p>
      </div>

      {/* Review Dialog */}
      <ReviewDialog
        isOpen={showReviewDialog}
        onClose={() => setShowReviewDialog(false)}
        onReview={handleReview}
        isLoading={isReviewing}
        action={reviewAction}
      />
    </div>
  );
}
